import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react'

// interface BottomSheetProps {
//   /** The content inside the bottom sheet */
//   children: React.ReactNode;

//   /** Duration of the animation in milliseconds (default: 300ms) */
//   animationDuration?: number;

//   /** The background color and opacity of the backdrop (default: 'bg-black bg-opacity-50') */
//   backdropColor?: string;

//   /** Callback function triggered when the bottom sheet is closed */
//   onClose?: () => void;

//   /** Callback function triggered when the bottom sheet is opened */
//   onOpen?: () => void;

//   /** Whether the bottom sheet can be dismissed by clicking the backdrop (default: true) */
//   canBeDismissed?: boolean;

//   /** Position of the bottom sheet (default: 'bottom') */
//   position?: 'bottom' | 'left' | 'right';

//   /** Height of the bottom sheet (default: 'max-h-[80vh]') */
//   height?: string;

//   /** The opacity of the backdrop (default: 0.5) */
//   backdropOpacity?: number;

//   /** Callback function triggered when the backdrop is clicked */
//   onBackdropClick?: () => void;

//   /** The height percentage at which the BottomSheet becomes scrollable (default: 80) */
//   scrollThreshold?: number;

//   /** Timing function for the animation (default: 'ease-in-out') */
//   animationType?: 'ease-in' | 'ease-out' | 'linear';

//   /** Whether the BottomSheet should adjust when the keyboard is visible on mobile devices (default: false) */
//   keyboardAvoidance?: boolean;

//   /** Custom border radius for the top corners of the BottomSheet (default: '16px') */
//   borderRadius?: string;
// }

// export interface BottomSheetRef {
//   /** Function to open the BottomSheet */
//   open: () => void;

//   /** Function to hide (close) the BottomSheet */
//   hide: () => void;
// }

const BottomSheet = forwardRef(
  (
    {
      children,
      animationDuration = 300,
      backdropColor = 'bg-black bg-opacity-50',
      onClose,
      onOpen,
      canBeDismissed = true,
      position = 'bottom',
      height = 'max-h-[80vh]',
      backdropOpacity = 0.5,
      onBackdropClick,
      scrollThreshold = 80,
      animationType = 'ease-in-out',
      keyboardAvoidance = false,
      borderRadius = '16px',
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [backdropVisible, setBackdropVisible] = useState(false)
    const sheetRef = useRef(null)
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)

    // Expose open and hide functions via ref
    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
      hide: () => {
        if (canBeDismissed) {
          handleClose()
        }
      },
    }))

    const handleClose = () => {
      if (onClose) onClose()
      setIsOpen(false)
      setBackdropVisible(false)
    }

    const handleBackdropClick = () => {
      if (onBackdropClick) onBackdropClick()
      else if (canBeDismissed) handleClose()
    }

    // Handle when the sheet opens
    useEffect(() => {
      if (isOpen) {
        setBackdropVisible(true)
        if (onOpen) onOpen() // Call onOpen when the sheet opens
      }
    }, [isOpen, onOpen])

    // Handle keyboard visibility and adjust the BottomSheet position
    useEffect(() => {
      if (keyboardAvoidance) {
        const onFocus = () => setIsKeyboardVisible(true)
        const onBlur = () => setIsKeyboardVisible(false)

        // Listen for focus events on input fields to detect keyboard opening
        window.addEventListener('focusin', onFocus)
        window.addEventListener('focusout', onBlur)

        // Clean up listeners
        return () => {
          window.removeEventListener('focusin', onFocus)
          window.removeEventListener('focusout', onBlur)
        }
      }
    }, [keyboardAvoidance])

    return (
      <>
        {/* Backdrop with fade-in animation and custom z-index */}
        {isOpen && (
          <div
            className={`fixed w-full inset-0 z-50 transition-opacity duration-${animationDuration} ${
              backdropVisible ? 'opacity-100' : 'opacity-0'
            } ${backdropColor}`}
            style={{ opacity: backdropOpacity }}
            onClick={handleBackdropClick}
          ></div>
        )}

        {/* Bottom Sheet with custom z-index, rounded top corners, and keyboard avoidance */}
        <div
          ref={sheetRef}
          className={`fixed ${position === 'bottom' ? 'bottom-0' : position === 'left' ? 'left-0' : 'right-0'} max-w-lg lg:max-w-xl w-full mx-auto left-0 right-0 ${height} bg-white z-50 overflow-auto transition-transform max-h-[90vh] duration-${animationDuration} lg:rounded-t-2xl ${
            isOpen ? `transform translate-y-0` : `transform translate-y-full`
          }`}
          style={{
            transitionDuration: `${animationDuration}ms`,
            transitionTimingFunction: animationType,
            borderTopLeftRadius: borderRadius,
            borderTopRightRadius: borderRadius,
            transform:
              isKeyboardVisible && keyboardAvoidance
                ? 'translateY(-20vh)'
                : undefined, // Adjust position when keyboard is visible
          }}
        >
          {/* Main content (children) */}
          <div
            className="h-full overflow-y-auto"
            style={{
              maxHeight: scrollThreshold ? `${scrollThreshold}%` : '80vh',
            }}
          >
            {children}
          </div>
        </div>
      </>
    )
  },
)

BottomSheet.displayName = 'BottomSheet'

export default BottomSheet
